import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";

const offers = [
  {
    n: "01",
    name: "Authority Gap Score",
    tagline: "The diagnostic. Find your gap in 3 minutes.",
    delivers: [
      "Authority Gap Score (0-100)",
      "Six-dimension breakdown",
      "Biggest gap identified",
      "Recommended next step",
    ],
    price: "Free",
    note: "No credit card",
    cta: { type: "internal", label: "Take the Score", to: "/score", variant: "primary" },
  },
  {
    n: "02",
    name: "Authority Audit",
    tagline: "AI-generated. In your inbox in minutes.",
    delivers: [
      "Full scored audit (7 dimensions)",
      "Top 3 gaps with priorities",
      "Top 3 opportunities with effort/impact",
      "Branded PDF you can share",
    ],
    price: "$49",
    note: "One-time",
    cta: { type: "internal", label: "Get an Audit", to: "/audit", variant: "primary" },
  },
  {
    n: "03",
    name: "Authority Blueprint",
    tagline: "The 90-day plan. Tailored to your business.",
    delivers: [
      "Positioning statement",
      "Full Authority Gap analysis",
      "Ecosystem map (which assets to build first)",
      "Content strategy and lead magnet plan",
      "Tool opportunities",
      "Product ladder",
      "Monetization plan",
      "90-day roadmap",
    ],
    price: "$497",
    note: "One-time",
    cta: { type: "internal", label: "Get a Blueprint", to: "/blueprint", variant: "primary" },
  },
  {
    n: "04",
    name: "Authority Launch Kit",
    tagline: "The foundation. Assets built for you.",
    delivers: [
      "Brand identity",
      "Website (5 core pages)",
      "Lead magnet created",
      "Email sequence (5 emails)",
      "25 SEO article outlines",
      "AI assistant built",
      "Analytics configured",
      "Clean handoff — no monthly retainer",
    ],
    price: "$2,500-$5,000",
    note: "One-time",
    cta: { type: "internal", label: "Start the Launch Kit", to: "/launch-kit", variant: "primary" },
  },
  {
    n: "05",
    name: "AuthorityOS Build",
    tagline: "The full treatment. Your ecosystem built.",
    delivers: [
      "Complete authority ecosystem designed and built",
      "Custom tools and AI assistant",
      "Content engine set up",
      "Monetization activated",
      "Automation workflows",
      "30-90 day support",
      "Full training and handoff",
    ],
    price: "$7,500-$25,000+",
    note: "Based on scope",
    cta: { type: "external", label: "Let's Talk", href: "mailto:support@theauthoritygap.co?subject=AuthorityOS%20Build", variant: "ghost" },
  },
];

const faqs = [
  {
    q: "How do I know which offer is right for me?",
    a: "Start with the free Authority Gap Score. The result maps directly to a recommended next step.",
  },
  {
    q: "How long does delivery take?",
    a: "Authority Audit ($49): minutes — AI-generated and emailed as a branded PDF. Authority Blueprint ($497): minutes — same pipeline, longer intake. Launch Kit ($2,500): 2 weeks. AuthorityOS Build ($7,500+): 30-90 days depending on scope.",
  },
  {
    q: "Do you work with any niche?",
    a: "Yes. AuthorityOS has been proven across healthcare, education, finance, compliance, sports, and enterprise. If you have deep expertise and thin online presence, we can build your ecosystem.",
  },
  {
    q: "What makes this different from an SEO agency?",
    a: "SEO agencies sell one tactic. AuthorityOS builds seven interconnected asset types that compound together. You own everything built.",
  },
  {
    q: "Can I start with a Blueprint and do the build myself?",
    a: "Yes. Many clients take the Blueprint and implement with their own team. The Blueprint gives you the full strategy regardless of who executes it.",
  },
];

const Section = ({ children, style }) => (
  <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "44px 32px", ...style }}>
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>{children}</div>
  </section>
);

export default function Work() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <Layout title="Work With Us | The Authority Gap" path="/work">
      <section style={{ background: "#090909", padding: "70px 32px 48px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 880, margin: "0 auto" }}>
          <div style={{ marginBottom: 24 }}><EyebrowPill>The Offer Ladder</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 18 }}>
            <span>Five ways to close</span><br />
            <span style={{ color: "#999" }}>your Authority Gap.</span>
          </h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>Start free. Go as deep as you need. Every step you take compounds — and you own everything we build.</p>
        </div>
      </section>

      <Section>
        {offers.map((o) => (
          <div
            key={o.n}
            data-testid={`offer-${o.n}`}
            style={{
              background: "#0f0f0f",
              border: "1px solid #1a1a1a",
              borderRadius: 10,
              padding: 24,
              marginBottom: 12,
              display: "grid",
              gridTemplateColumns: "1fr 200px",
              gap: 24,
              alignItems: "center",
            }}
            className="tag-offer-card"
          >
            <div>
              <div style={{ fontSize: 9, color: "#FF6575", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>
                Offer {o.n}
              </div>
              <h3 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>{o.name}</h3>
              <p style={{ color: "#aaa", fontSize: 13, fontStyle: "italic", margin: "0 0 12px" }}>{o.tagline}</p>
              <div style={{ color: "#888", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>
                Delivers
              </div>
              {o.delivers.map((d) => (
                <div key={d} style={{ display: "flex", gap: 6, alignItems: "baseline", marginBottom: 4 }}>
                  <span style={{ width: 3, height: 3, background: "#FF6575", borderRadius: "50%", flexShrink: 0, marginTop: 6 }} />
                  <span style={{ color: "#bbb", fontSize: 12 }}>{d}</span>
                </div>
              ))}
            </div>
            <div style={{ minWidth: 160, textAlign: "right" }}>
              <div style={{ color: "#fff", fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em" }}>{o.price}</div>
              <div style={{ color: "#555", fontSize: 11, marginTop: 4, marginBottom: 16 }}>{o.note}</div>
              {o.cta.type === "internal" ? (
                <Link to={o.cta.to} data-testid={`offer-cta-${o.n}`} className={o.cta.variant === "primary" ? "btn-primary" : "btn-ghost"}>
                  {o.cta.label}
                </Link>
              ) : (
                <a href={o.cta.href} data-testid={`offer-cta-${o.n}`} className={o.cta.variant === "primary" ? "btn-primary" : "btn-ghost"}>
                  {o.cta.label}
                </a>
              )}
            </div>
          </div>
        ))}
        <style>{`
          @media (max-width: 720px) {
            .tag-offer-card { grid-template-columns: 1fr !important; }
            .tag-offer-card > div:last-child { text-align: left !important; }
          }
        `}</style>
      </Section>

      <Section>
        <h2 className="tag-h2" style={{ marginBottom: 24 }}>Common questions</h2>
        <div>
          {faqs.map((f, i) => (
            <div key={f.q} style={{ borderBottom: "1px solid #1a1a1a" }}>
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "16px 0",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontFamily: "var(--font-body)",
                }}
              >
                <span>{f.q}</span>
                <span style={{ color: "#FF6575", fontSize: 20, fontWeight: 300 }}>{openFaq === i ? "−" : "+"}</span>
              </button>
              {openFaq === i && (
                <div style={{ color: "#bbb", fontSize: 13, lineHeight: 1.7, paddingBottom: 16 }} data-testid={`faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
    </Layout>
  );
}
